import React, { useState, useEffect } from 'react';
import { Modal, Input, Row, Col, Switch, message } from 'antd';
import useCreateSupportChatModuleTagMutation from './queries/useCreateSupportChatModuleTagMutation';
import useUpdateSupportChatModuleTagMutation from './queries/useUpdateSupportChatModuleTagMutation';
import useGetSupportChatModuleTagsQuery from './queries/useGetSupportChatModuleTagsQuery';

/**
 * Validates tag name
 * @param {string} name - Tag name to validate
 * @param {Array} existingTags - Array of existing tags
 * @param {number} [excludeId] - Optional tag ID to exclude from duplicate check
 * @returns {string} Error message or empty string if valid
 */
const validateTagName = (name, existingTags, excludeId = null) => {
  if (!name || typeof name !== 'string') {
    return 'Tag name is required';
  }

  const trimmedName = name.trim();
  if (!trimmedName) {
    return 'Tag name cannot be empty';
  }

  // Check if name contains only special characters (no alphanumeric characters)
  const hasAlphanumeric = /[a-zA-Z0-9]/.test(trimmedName);
  if (!hasAlphanumeric) {
    return 'Tag name must contain at least one letter or number';
  }

  // Check for duplicate names (case-insensitive)
  const duplicateTag = existingTags.find(
    existingTag =>
      existingTag.id !== excludeId &&
      existingTag.name.trim().toLowerCase() === trimmedName.toLowerCase()
  );
  if (duplicateTag) {
    return 'Tag name already exists';
  }

  return '';
};

const EditSupportChatModuleTag = ({ tag, visible, setEditMode, editing }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isActive: true,
  });
  const [nameError, setNameError] = useState('');

  const createMutation = useCreateSupportChatModuleTagMutation();
  const updateMutation = useUpdateSupportChatModuleTagMutation();
  const { data: allTags = [] } = useGetSupportChatModuleTagsQuery();

  useEffect(() => {
    if (editing && tag) {
      setFormData({
        name: tag.name || '',
        description: tag.description || '',
        isActive: tag.isActive !== undefined ? tag.isActive : true,
      });
      // Validate name when editing
      const error = validateTagName(tag.name || '', allTags, tag.id);
      setNameError(error);
    } else {
      setFormData({
        name: '',
        description: '',
        isActive: true,
      });
      setNameError('');
    }
  }, [tag, editing, visible, allTags]);

  const onChange = field => e => {
    const value = e && e.target ? e.target.value : e;
    setFormData(prev => ({ ...prev, [field]: value }));

    // Validate name in real-time
    if (field === 'name') {
      const error = validateTagName(
        value,
        allTags,
        editing && tag?.id ? tag.id : null
      );
      setNameError(error);
    }
  };

  const handleSubmit = () => {
    // Validate required fields
    const name = formData.name?.trim();
    const description = formData.description?.trim();

    if (!name) {
      message.error('Name is required');
      return;
    }

    if (!description) {
      message.error('Description is required');
      return;
    }

    // Validate tag name
    const nameValidationError = validateTagName(
      name,
      allTags,
      editing && tag?.id ? tag.id : null
    );
    if (nameValidationError) {
      setNameError(nameValidationError);
      message.error(nameValidationError);
      return;
    }

    // Clear any previous errors
    setNameError('');

    // Ensure required fields are trimmed
    const payload = {
      name,
      description,
      isActive: formData.isActive !== undefined ? formData.isActive : true,
    };

    // Add id for update operations
    if (editing && tag?.id) {
      payload.id = tag.id;
    }

    setEditMode(false);

    if (editing) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
  };

  return (
    <Modal
      title={
        editing ? 'Edit Support Chat Module Tag' : 'Add Support Chat Module Tag'
      }
      visible={visible}
      onOk={handleSubmit}
      onCancel={handleCancel}
      width={600}
      confirmLoading={createMutation.isLoading || updateMutation.isLoading}
    >
      <div className="editor-modal">
        <Row className="edit-field" style={{ marginBottom: '16px' }}>
          <Col span={6}>Name:</Col>
          <Col span={18}>
            <Input
              value={formData.name}
              onChange={onChange('name')}
              placeholder="Enter tag name"
              status={nameError ? 'error' : ''}
            />
            {nameError && (
              <div
                style={{ color: '#ff4d4f', marginTop: '4px', fontSize: '14px' }}
              >
                {nameError}
              </div>
            )}
          </Col>
        </Row>
        <Row className="edit-field" style={{ marginBottom: '16px' }}>
          <Col span={6}>Description:</Col>
          <Col span={18}>
            <Input.TextArea
              value={formData.description}
              onChange={onChange('description')}
              placeholder="Enter tag description"
              rows={4}
            />
          </Col>
        </Row>
        <Row className="edit-field">
          <Col span={6}>Active:</Col>
          <Col span={18}>
            <Switch
              checked={formData.isActive !== false}
              onChange={onChange('isActive')}
            />
          </Col>
        </Row>
      </div>
    </Modal>
  );
};

export default EditSupportChatModuleTag;
